<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-card>
          <v-row no-gutters align="center">
            <v-col>
              <v-card-title>Case Manager</v-card-title>
              <v-card-subtitle>{{ getOrg }}</v-card-subtitle>
            </v-col>
            <v-col align="right">
              <v-btn
                class="mx-2"
                fab
                dark
                x-small
                color="green"
                :disabled="!hasPermission('CAN_ADMIN_CASES')"
                @click="onAddClick()">
                <v-icon>mdi-plus</v-icon>
              </v-btn>

              <v-btn
                class="mx-2 mr-4"
                fab
                dark
                x-small
                :loading="loading"
                color="primary"
                @click="loadTable()">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- filters -->
    <v-card outlined class="mt-3">
      <v-expansion-panels flat>
        <v-expansion-panel>
          <v-expansion-panel-header> Search and Filter </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- Row 1 -->
            <v-row dense align="center">
              <!-- Name -->
              <v-col cols="3">
                <v-text-field
                  label="Case Name"
                  v-model="name"
                  dense
                  clearable
                  class="text-body-1" />
              </v-col>
              <!-- Local ID  -->
              <v-col cols="2">
                <v-text-field label="ID" v-model="localId" dense clearable class="text-body-1" />
              </v-col>
              <!-- Case Group -->
              <v-col cols="4">
                <v-select
                  dense
                  label="Group"
                  v-model="caseGroups"
                  :items="getCaseGroups"
                  item-text="name"
                  item-value="id"
                  clearable
                  multiple
                  class="text-body-1" />
              </v-col>
              <v-col cols="3">
                <v-select
                  dense
                  label="Report Statuses"
                  v-model="statusFilter"
                  :items="getReportStatuses"
                  item-text="status"
                  item-value="code"
                  small-chips
                  deletable-chips
                  multiple
                  class="text-body-1" />
              </v-col>
            </v-row>
            <!-- Row 2  -->
            <v-row dense align="center">
              <!-- Type -->
              <v-col cols="2">
                <v-select
                  dense
                  :label="$t('label.type')"
                  v-model="type"
                  :items="getEntityTypes"
                  clearable
                  class="text-body-1" />
              </v-col>
              <v-col cols="3">
                <!-- Last screen date -->
                <v-menu
                  ref="screeningDateRange"
                  v-model="screeningDateRange"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dateRangeFormattedComputed"
                      label="Last Screening Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      dense
                      clearable
                      @click:clear="clearScreeningDates"
                      class="text-body-1"></v-text-field>
                  </template>
                  <v-date-picker v-model="dates" range no-title>
                    <v-spacer />
                    <v-btn text color="primary" @click="screeningDateRange = false"> Cancel </v-btn>
                    <v-btn text color="primary" @click="$refs.screeningDateRange.save(dates)">
                      OK
                    </v-btn>
                  </v-date-picker>
                </v-menu>
              </v-col>
              <!-- Country -->
              <v-col cols="3">
                <v-autocomplete
                  v-model="countryFilter"
                  :items="countries"
                  item-text="name"
                  item-value="alpha2"
                  dense
                  label="Country"
                  multiple
                  clearable
                  class="text-body-1"
                  @input="searchInputCountry = null"
                  :search-input.sync="searchInputCountry"></v-autocomplete>
              </v-col>
            </v-row>

            <!-- Client defined case labels -->
            <v-row dense align="center" v-if="getAccountSettings.customLabels">
              <v-col v-for="(value, labelName) in getAccountSettings.customLabels" :key="labelName">
                <v-select
                  :items="value"
                  :label="labelName"
                  v-model="labelFilter[labelName]"
                  multiple
                  clearable
                  dense
                  small-chips
                  deletable-chips
                  class="text-body-2"></v-select>
              </v-col>
            </v-row>

            <!-- Search -->
            <v-row dense align="center">
              <!-- View archived -->
              <v-col cols="3" class="d-flex">
                <v-switch
                  height="1"
                  inset
                  dense
                  v-model="filterArchived"
                  label="Filter Archived"
                  color="primary" />
              </v-col>
              <v-spacer />
              <v-col align="right" cols="1">
                <v-btn fab dark x-small @click="loadTable()" :loading="loading">
                  <v-icon dark>mdi-magnify</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>

    <!-- table -->
    <v-row dense class="mt-3">
      <v-col>
        <v-data-table
          dense
          :headers="headers"
          :items="casesPage.cases"
          :options.sync="options"
          :server-items-length="casesPage.totalCases"
          :footer-props="footerProps"
          v-model="selectedCases"
          item-key="id"
          :item-class="getRowAlertClass"
          :loading="loading"
          show-select
          no-data-text="No cases">
          <template v-slot:top>
            <v-toolbar flat>
              <v-spacer />
              <v-menu offset-y max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="primary" dark v-bind="attrs" v-on="on">
                    Actions
                    <v-icon dark right>mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <!-- Screen selected -->
                  <v-list-item
                    link
                    @click="onScreenClick(selectedCases)"
                    :disabled="selectedCases.length < 1">
                    <v-list-item-icon>
                      <v-icon>mdi-magnify</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Screen Selected</v-list-item-title>
                      <v-list-item-subtitle> Screens selected cases </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- archive -->
                  <v-list-item
                    link
                    @click="onArchiveClick({ archive: true })"
                    :disabled="!hasPermission('CAN_ADMIN_CASES') || selectedCases.length < 1">
                    <v-list-item-icon>
                      <v-icon>mdi-delete</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title
                        :class="
                          hasPermission('CAN_ADMIN_CASES') && selectedCases.length > 0
                            ? 'red--text'
                            : ''
                        "
                        >Archive Selected</v-list-item-title
                      >
                      <v-list-item-subtitle class="text-wrap">
                        Archives selected cases. They will no longer be displayed cases table or be
                        included in ongoing screening. They will remain in history
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- bulk group assign/unassign -->
                  <v-list-item
                    link
                    @click="onBulkCaseGroupSetOpen"
                    :disabled="!hasPermission('CAN_ADMIN_CASES') || selectedCases.length < 1">
                    <v-list-item-icon>
                      <v-icon> mdi-account-multiple </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Edit Case Group</v-list-item-title>
                      <v-list-item-subtitle class="text-wrap"
                        >Update or remove Case Group settings for the selected
                        Cases</v-list-item-subtitle
                      >
                    </v-list-item-content>
                  </v-list-item>
                  <!-- CSV Export -->
                  <v-list-item link @click="onCsvExport()" :disabled="casesPage.totalCases < 1">
                    <v-list-item-icon>
                      <v-icon color="primary"> mdi-download </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Export Case Information</v-list-item-title>
                      <template v-if="casesPage.totalCases > 1">
                        <v-list-item-subtitle class="text-wrap"
                          >Export a CSV of Case information based on current search
                          parameters</v-list-item-subtitle
                        >
                      </template>
                      <template v-else>
                        <v-list-item-subtitle class="text-wrap"
                          >No data to export. Adjust your search to include more
                          information</v-list-item-subtitle
                        >
                      </template>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-toolbar>
          </template>

          <template v-slot:item.name="{ item }">
              <span class="text-no-wrap" v-if="!item.archived">{{ item.name }}</span>
              <span v-else class="text-no-wrap font-italic text--disabled">
                {{ item.name }}
              </span>
          </template>

          <template v-slot:item.type="{ item }">
            <TypeIcon :type="item.type" :includeText="false" />
          </template>

          <template v-slot:item.caseGroupId="{ item }">
            {{
              item.caseGroupId == null || item.caseGroupId === undefined
                ? ""
                : getCaseGroupName(item.caseGroupId)
            }}
          </template>
          <template v-slot:item.createdOn="{ item }">
            {{ formatDate(item.createdOn) }}
          </template>

          <template v-slot:item.lastScreenDate="{ item }">
            {{ getTimeSince(item.lastScreenDate) }}
          </template>

          <template v-slot:item.controls="{ item, index }">
            <v-row dense>
              <v-col>
                <v-btn
                  icon
                  x-small
                  color="green"
                  :disabled="item.archived"
                  @click="onViewClick(item, index)">
                  <v-icon dark>mdi-magnify</v-icon>
                </v-btn>
              </v-col>
              <!-- <v-col>
                <v-btn icon x-small color="black">
                  <v-icon dark>mdi-file-pdf-box</v-icon>
                </v-btn>
              </v-col> -->
              <v-col>
                <v-btn
                  icon
                  x-small
                  color="primary"
                  :disabled="item.archived || !hasPermission('CAN_ADMIN_CASES')"
                  @click="onEditClick(item)">
                  <v-icon dark>mdi-pencil</v-icon>
                </v-btn>
              </v-col>
              <v-col v-if="!item.archived">
                <v-btn
                  icon
                  x-small
                  color="red"
                  :disabled="!hasPermission('CAN_ADMIN_CASES')"
                  @click="onArchiveClick({ caseId: item.id, archive: true })">
                  <v-icon dark title="Archive">mdi-delete</v-icon>
                </v-btn>
              </v-col>

              <v-col v-else>
                <v-btn
                  icon
                  x-small
                  color="green"
                  :disabled="!hasPermission('CAN_ADMIN_CASES')"
                  @click="onArchiveClick({ caseId: item.id, archive: false })">
                  <v-icon dark title="Unarchive">mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <ViewCase
      :selectedCase="selectedCase"
      v-model="isViewDialogVisible"
      v-on:openCaseReport="openCaseReport"
      v-on:onClose="onViewCaseClose" />

    <AddCase
      :visible="isAddDialogVisible"
      :key="addCaseComponentKey"
      v-on:onConfirmed="onAddCaseConfirmed"
      v-on:onCancelled="onAddCaseClose"
      :caseGroups="getCaseGroups" />

    <EditCase
      :selectedCase="selectedCase"
      :visible="isEditDialogVisible"
      :caseGroups="getCaseGroups"
      v-on:onConfirmed="onEditCaseClose"
      v-on:onCancelled="onEditCaseClose" />

    <v-dialog v-model="caseReportDialog">
      <v-sheet>
        <CaseReport
          :selectedCase="selectedCase"
          :selectedCaseReport="selectedCaseReport"
          v-on:onClose="onCaseReportClose"
          v-on:caseReportUpdated="caseReportUpdated" />
      </v-sheet>
    </v-dialog>

    <BulkCaseGroupSet
      :visible="isBulkCaseGroupSetVisible"
      :caseGroups="getCaseGroups"
      v-on:onConfirm="onBulkCaseGroupSetConfirm"
      v-on:onClose="onBulkCaseGroupSetCancel" />

    <Confirm ref="confirm" />
  </v-container>
</template>

<script>
// import axios from "axios";
import { getCaseApi, getCasesApi, screenCasesApi, bulkPatchCasesApi, downloadCaseCsvApi } from "../api/caseApi";
import { mapGetters, mapActions } from "vuex";
import { formatDate, formatDateTime, dateRangeFormatted, getTimeSince } from "../utils/dateUtils";
import { getCaseGroupName } from "../utils/caseGroupUtils";
import countriesjson from "../assets/countries.json";
import { hasOpenCaseReports } from "../utils/caseUtils";
import { hasPermission } from "../utils/permission";
import AddCase from "../components/case/AddCase.vue";
import EditCase from "../components/case/EditCase.vue";
import ViewCase from "../components/case/ViewCase.vue";
import Confirm from "../modals/Confirm";
import CaseReport from "./CaseReport.vue";
import TypeIcon from "../components/TypeIcon.vue";
import BulkCaseGroupSet from "../modals/BulkCaseGroupSet.vue";

export default {
  name: "CaseManager",
  components: {
    AddCase,
    EditCase,
    ViewCase,
    Confirm,
    CaseReport,
    TypeIcon,
    BulkCaseGroupSet,
  },
  props: {
    id: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      selectedCase: {}, // Populated when a table action is clicked
      selectedCases: [], // Populated by table multi-select
      casesPage: {
        cases: [],
        totalCases: 0,
      },
      loading: false,
      modalLoading: false,
      caseReportDialog: false,
      selectedCaseReport: {},
      addCaseComponentKey: 0,

      // search
      localId: "",
      name: "",
      statusFilter: [],
      countries: countriesjson,
      countryFilter: [],
      labelFilter: {},
      searchInputCountry: null,
      caseGroups: [],
      type: "",
      filterArchived: true,
      dates: [],

      // pagination
      options: {},
      footerProps: {
        "items-per-page-options": [10, 20, 50, 200],
        "show-first-last-page": true,
      },

      screeningDateRange: false,
      isAddDialogVisible: false,
      isEditDialogVisible: false,
      isViewDialogVisible: false,
      isBulkCaseGroupSetVisible: false,
      error: "",
      headers: [
        { text: "Name", value: "name" },
        { text: "ID", value: "localId", width: 100 },
        { text: "Type", value: "type" }, // Should be an icon (person, building, group, vessel)
        { text: "Group", value: "caseGroupId" },
        // { text: "Last Screening", value: "lastScreenDate" },
        { text: "Created", value: "createdOn" },
        { text: "Last Screened", value: "lastScreenDate" },
        {
          text: "Admin",
          value: "controls",
          sortable: false,
          align: "right",
          width: 140,
        },
      ],
    };
  },
  async mounted() {
    if (this.id) {
      // console.log("ID passed to CaseManager: " + this.id);
      this.loading = true;
      try {
        const response = await this.getCaseApi(this.id);
        this.onViewClick(response);
      } catch (error) {
        console.error("Error fetching case by ID (path)", error);
        this.setGlobalAlert({
          alert: true,
          color: "red",
          alertTitle: "Oops.",
          alertMessage: "Unable to get cases at this time. Please try again later.",
        });
      } finally {
        this.selectedCases = [];
        this.loading = false;
      }
    }
  },
  computed: {
    ...mapGetters([
      "getOrg",
      "getCaseGroups",
      "getAccountSettings",
      "getEntityTypes",
      "getReportStatuses",
    ]),
    dateRangeFormattedComputed: {
      get: function () {
        return this.dateRangeFormatted(this.dates[0], this.dates[1]);
      },
      set: function (newDates) {
        newDates;
      },
    },
  },
  watch: {
    options: {
      handler() {
        this.loadTable();
      },
      deep: true,
    },
  },
  methods: {
    hasPermission,
    formatDate,
    formatDateTime,
    dateRangeFormatted,
    getTimeSince,
    getCaseApi,
    getCasesApi,
    screenCasesApi,
    bulkPatchCasesApi,
    downloadCaseCsvApi,
    getCaseGroupName,
    hasOpenCaseReports,
    ...mapActions(["setGlobalAlert", "getUsers"]),
    async loadTable() {
      this.loading = true;
      // Build Params
      var params = this.getSearchRequest();
      try {
        const response = await this.getCasesApi(params);
        this.casesPage = response;
      } catch (error) {
        this.setGlobalAlert({
          alert: true,
          color: "red",
          alertTitle: "Oops.",
          alertMessage: "Unable to get cases at this time. Please try again later.",
        });
      } finally {
        this.selectedCases = [];
        this.loading = false;
      }
    },
    clearScreeningDates() {
      this.dates = [];
    },
    onAddClick: function () {
      this.isAddDialogVisible = true;
    },
    onEditClick: function (selectedCase) {
      this.selectedCase = selectedCase;
      this.isEditDialogVisible = true;
    },
    onViewClick: function (selectedCase) {
      this.selectedCase = selectedCase;
      this.isViewDialogVisible = true;
    },
    onAddCaseConfirmed() {
      this.isAddDialogVisible = false;
      // this.addCaseComponentKey++;
      // Reload the table instead of added the new case as the table is paginated/filtered.
      this.loadTable();
    },
    onAddCaseClose() {
      this.isAddDialogVisible = false;
    },
    onEditCaseClose(updatedCaseRecord) {
      if (updatedCaseRecord) {
        const indexToReplace = this.casesPage.cases.findIndex(
          (record) => record.id === updatedCaseRecord.id
        );
        if (indexToReplace !== -1) {
          // this.casesPage.cases[indexToReplace] = updatedCaseRecord;
          this.$set(this.casesPage.cases, indexToReplace, updatedCaseRecord);
        }
      }
      this.isEditDialogVisible = false;
      this.selectedCase = {}; // Note this DOESN'T destroy the component.
    },
    // Comes from ViewCase modal with CaseReport to open.
    openCaseReport(caseReport) {
      this.selectedCaseReport = caseReport;
      this.caseReportDialog = true;
    },
    caseReportUpdated(updatedCaseReport) {
      const index = this.selectedCase.caseReports.findIndex(
        (caseReport) => updatedCaseReport.id === caseReport.id
      );
      this.$set(this.selectedCase.caseReports, index, updatedCaseReport); // This is required so the change is picked up by the watcher in ViewCase when an update comes in from CaseReport modal.
      this.selectedCaseReport = updatedCaseReport;
    },
    onCaseReportClose() {
      // this.caseReport = {}
      this.caseReportDialog = false;
    },
    onViewCaseClose() {
      this.isViewDialogVisible = false;
      this.selectedCase = {}; // Destroys the component in order to re call created() to init attributes
    },
    onBulkCaseGroupSetOpen() {
      this.isBulkCaseGroupSetVisible = true;
    },
    onBulkCaseGroupSetCancel() {
      this.isBulkCaseGroupSetVisible = false;
    },
    onArchiveClick: function ({ caseId, archive }) {
      var params = { archive: archive };

      if (caseId) {
        params.ids = [caseId];
      } else {
        params.ids = this.selectedCases.map((caseRecord) => caseRecord.id);
      }

      if (archive) {
        // Show confirm for archiving action only.
        this.$refs.confirm
          .open(
            "Archive " + params.ids.length + " cases?",
            "Archiving cases excludes them from scheduled recurring screening and they will not be visible in this table.",
            { color: "red" }
          )
          .then((confirm) => {
            if (confirm) {
              this.callBulkPatchCases(params);
            }
          });
      } else {
        this.callBulkPatchCases(params);
      }
    },
    onBulkCaseGroupSetConfirm: async function (caseGroup) {
      var ids = this.selectedCases.map((aCase) => aCase.id);
      var params = { ids: ids };
      params.caseGroupId = caseGroup.id;

      await this.callBulkPatchCases(params).then(() => {
        this.isBulkCaseGroupSetVisible = false;
      });
    },
    onScreenClick: async function (selectedCases) {
      if (selectedCases.length > 10) {
        this.setGlobalAlert({
          alert: true,
          color: "black",
          alertTitle: "Bulk screening is limited to 10 cases",
          alertMessage: "Please reduce the number of selected cases and try again.",
        });
        return;
      }
      this.loading = true;
      var ids = selectedCases.map((aCase) => aCase.id);
      try {
        const response = await this.screenCasesApi(ids);
        this.casesPage.cases = this.mergeCaseRecords(this.casesPage.cases, response);
      } catch (error) {
        console.log("Error screening selected cases.", error);
        this.setGlobalAlert({
          alert: true,
          color: "red",
          alertTitle: "Oops.",
          alertMessage: "Unable to screen selected cases. Please try again later.",
        });
      } finally {
        this.loading = false;
        this.setGlobalAlert({
          alert: true,
          color: "green",
          alertTitle: "Complete",
          alertMessage: selectedCases.length + " cases screened.",
        });
      }
    },
    callBulkPatchCases: async function (params) {
      this.loading = true;
      try {
        const modified = await this.bulkPatchCasesApi(params);
        this.selectedCases = [];
        this.loadTable().then(() => {
          this.setGlobalAlert({
            alert: true,
            color: "green",
            alertMessage: modified + " cases updated",
          });
        });
      } catch (error) {
        this.setGlobalAlert({
          alert: true,
          color: "red",
          alertMessage: "Error updating cases, please try again.",
        });
      } finally {
        this.loading = false;
      }
    },
    mergeCaseRecords(oldList, updatedList) {
      const updatedMap = new Map();
      for (const obj of updatedList) {
        updatedMap.set(obj.id, obj);
      }
      const newList = [];
      for (const oldObj of oldList) {
        if (updatedMap.has(oldObj.id)) {
          newList.push(updatedMap.get(oldObj.id));
        } else {
          newList.push(oldObj);
        }
      }
      return newList;
    },
    async onCsvExport() {
      if (this.casesPage.totalCases > 15000) {
        this.setGlobalAlert({
          alert: true,
          alertTitle: "Maximum Case Records",
          alertMessage: "Please narrow your search criteria to proceed with CSV export.",
        });
        return;
      } else {
        try {
          this.loading = true;
          var params = this.getSearchRequest();
          await this.downloadCaseCsvApi(params)
        } catch (error) {
          this.setGlobalAlert({
            alert: true,
            color: "error",
            alertTitle: "Error",
            alertMessage:
              "There's been an error generating the CSV report. Please try again shortly.",
          });
        } finally {
          this.loading = false;
        }
      }
    },
    getSearchRequest() {
      var params = {};
      // Page
      params.itemsPerPage = this.options.itemsPerPage;
      params.page = this.options.page - 1; // API expects 0 index
      if (this.options.sortBy[0]) {
        params.sortBy = this.options.sortBy[0];
        params.sortDesc = this.options.sortDesc[0];
      }

      // Query
      if (this.localId) params.localId = this.localId;
      if (this.name) params.name = this.name;
      if (this.caseGroups) {
        params.groupId = this.caseGroups;
      }
      if (this.statusFilter) {
        params.status = this.statusFilter;
      }
      if (this.type) params.type = this.type;
      if (this.filterArchived) {
        params.archived = false;
      } else {
        params.archived = true;
      }
      if (this.dates[0]) params.screenedAfter = this.dates[0];
      if (this.dates[1]) params.screenedBefore = this.dates[1];
      if (this.countryFilter) {
        params.countries = this.countryFilter;
      }
      params.label = this.labelFilter;
      return params;
    },
    getRowAlertClass(item){
      if(this.hasOpenCaseReports(item)){
        return 'red lighten-5';
        // return 'open-reports'
      } else {
        return ''
      }
    }
  },
  async created() {
    if (this.getAccountSettings && this.getAccountSettings.customLabels) {
      var headerIndex = 2; // Add after name, ID, etc.
      for (const key in this.getAccountSettings.customLabels) {
        if (Object.prototype.hasOwnProperty.call(this.getAccountSettings.customLabels, key)) {
          this.headers.splice(headerIndex, 0, { text: key, value: "customLabels." + key });
          headerIndex++;
        }
      }
    }
  },
};
</script>

<style >
    .open-reports {
      background-color: #FFCCCC; /* Example highlight color */
    }
    </style>